import { useCallback, useEffect, useState } from "react";
import type { Editor } from "@tinymce/tinymce-react";
import { MenuBorderColor } from "shared/constants";
import { selectionCrossesSsmlBoundaries } from "shared/utils/menu-disabling-rules";

export type SelectionState = "none" | "partial" | "full";

export function useSelectionBanner(editorRef: React.RefObject<Editor>) {
  const [selectionState, setSelectionState] = useState<SelectionState>("none");

  const checkSelectionInsideTag = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    const selectedNode = editor.selection.getNode();
    const parentSSMLNode = selectedNode.closest("span[data-ssml]");

    if (parentSSMLNode) {
      const dataSsml = parentSSMLNode.getAttribute("data-ssml");

      // Check if it's a break tag - don't enter nesting mode
      if (dataSsml?.startsWith("break-")) {
        setSelectionState("none");
        return;
      }

      // For all other tags, show banner
      const hasTextSelection = !editor.selection.isCollapsed();
      if (hasTextSelection) {
        if (selectionCrossesSsmlBoundaries(editorRef)) {
          setSelectionState("partial");
        } else {
          setSelectionState("full");
        }
      } else {
        setSelectionState("none");
      }
    } else {
      setSelectionState("none");
    }
  }, [editorRef]);

  const updateEditorBorder = useCallback(() => {
    const editorContainer = document.querySelector(
      ".tox-tinymce"
    ) as HTMLElement;
    if (editorContainer) {
      const borderColor =
        selectionState !== "none" ? "rgba(2, 136, 209, 1)" : MenuBorderColor;
      editorContainer.style.border = `1px solid ${borderColor}`;
      editorContainer.style.borderTop = "0";
      editorContainer.style.borderRadius = "0";
    }
  }, [selectionState]);

  useEffect(() => {
    const rafId = requestAnimationFrame(updateEditorBorder);
    return () => cancelAnimationFrame(rafId);
  }, [selectionState, updateEditorBorder]);

  return { selectionState, checkSelectionInsideTag, updateEditorBorder };
}
